import React, { useCallback, useMemo } from "react";
import { Column, Row } from "@tanstack/react-table";
import { SHTableRow } from "@components/design-systems";
import { useTheme } from "@mui/system";
import { MSTableDefaultSizeConstants } from "@pages/managed-accounts/model-screener/constant";
import { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import { ModelScreenerRow } from "@models/model-screener/entities/modelScreenerData";
import { MSTableStickyColumn } from "@pages/managed-accounts/model-screener/components/model-screener-table/table/table-body/table-body-row/sticky-column";
import { MSTableVirtualizedColumn } from "@pages/managed-accounts/model-screener/components/model-screener-table/table/table-body/table-body-row/virtualized-column";

type MSTableBodyRowProps = {
  row: Row<ModelScreenerRow>;
  virtualRow: VirtualItem<Element>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columns: Column<ModelScreenerRow>[];
  columnVirtualizerItems: VirtualItem<Element>[];
};

export const MSTableBodyRow = React.memo(
  ({
    row,
    virtualRow,
    rowVirtualizer,
    columns,
    columnVirtualizerItems,
  }: MSTableBodyRowProps) => {
    const { palette } = useTheme();

    const setRowRef = useCallback(
      (node: HTMLTableRowElement | null) => {
        if (node) rowVirtualizer.measureElement(node);
      },
      [rowVirtualizer],
    );

    const rowStyle: React.CSSProperties = useMemo(
      () => ({
        display: "flex",
        position: "absolute",
        transform: `translateY(${virtualRow.start}px)`,
        width: "100%",
        height: MSTableDefaultSizeConstants.CellHeight,
        backgroundColor:
          virtualRow.index % 2 === 0 ? palette.secondary[50] : "white",
      }),
      [virtualRow.start, virtualRow.index, palette.secondary],
    );

    return (
      <SHTableRow
        data-index={virtualRow.index}
        ref={setRowRef}
        style={rowStyle}
      >
        <MSTableStickyColumn row={row} columns={columns} />
        <MSTableVirtualizedColumn row={row} columnVirtualizerItems={columnVirtualizerItems} />
      </SHTableRow>
    );
  },
);
